import { useEffect, useState, FocusEvent } from 'react';
import { useMountedState } from 'react-use';
import debounce from 'lodash.debounce';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { isStringDigitsOnly } from 'CorePackage/MainModule/validators/isStringDigitsOnly';

import { generateLegalEntityLookupText } from '../common/generateLegalEntityLookupText';
import { useFnsLookupByOgrn } from '../hooks/useFnsLookupByOgrn';
import { LegalEntityAutoCompleteField, ILegalEntityAutoCompleteFieldBasicProps } from './LegalEntityAutoCompleteField';
import { ILegalEntityAutoCompleteOption } from '../interfaces/ILegalEntityAutoCompleteOption';

type IKnmOgrnFieldProps = ILegalEntityAutoCompleteFieldBasicProps & {
  onAutoCompleteChange?: (value: string) => void;
  onAutoCompleteSelect?: (legalEntityLookup: ILegalEntityLookup) => void;
  onLoadingChange?: (value: boolean) => void;
};

export const KnmOgrnField = ({
  onAutoCompleteChange,
  onAutoCompleteSelect,
  onLoadingChange,
  ...rest
}: IKnmOgrnFieldProps) => {
  const isMounted = useMountedState();
  const { isLoading: isFnsLookupByOgrnLoading, getFnsLookupByOgrn } = useFnsLookupByOgrn();
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<ILegalEntityAutoCompleteOption[] | undefined>();

  useEffect(() => {
    onLoadingChange && onLoadingChange(isFnsLookupByOgrnLoading);
  }, [isFnsLookupByOgrnLoading, onLoadingChange]);

  async function findOgrns(value?: string) {
    if (!value || value.length < 3) {
      setAutoCompleteOptions(undefined);
    } else {
      const valueEdited = value;

      if (valueEdited && (valueEdited.length === 13 || valueEdited.length === 15)) {
        if (!isStringDigitsOnly(valueEdited)) {
          return;
        }
        const getFnsLookupByOgrnResponse = await getFnsLookupByOgrn(valueEdited);

        if (!isMounted()) {
          return;
        }
        if (getFnsLookupByOgrnResponse.success && getFnsLookupByOgrnResponse.data) {
          const legalEntityLookup = getFnsLookupByOgrnResponse.data;
          const legalEntityLookupText = generateLegalEntityLookupText(legalEntityLookup);

          if (legalEntityLookupText) {
            setAutoCompleteOptions([
              {
                label: legalEntityLookupText,
                value,
                content: legalEntityLookup,
              },
            ]);
          } else {
            setAutoCompleteOptions([]);
          }
        } else {
          setAutoCompleteOptions([]);
        }
      }
    }
  }

  const debouncedUpdateAutoCompleteOptions = debounce(findOgrns, 2000);

  function handleAutoCompleteChange(value: string) {
    debouncedUpdateAutoCompleteOptions(value);
    if (onAutoCompleteChange) {
      onAutoCompleteChange(value);
    }
  }

  function handleAutoCompleteSelect(value: string) {
    return () => {
      debouncedUpdateAutoCompleteOptions();
      setAutoCompleteOptions(undefined);
      const currentOption = autoCompleteOptions?.find(item => item.value === value);
      const selectedLegalEntityLookup = currentOption?.content;

      onAutoCompleteSelect && selectedLegalEntityLookup && onAutoCompleteSelect(selectedLegalEntityLookup);
    };
  }

  function handleInputBlur(e: FocusEvent<HTMLInputElement>) {
    const tagName = (e?.relatedTarget as Element)?.tagName;

    if (tagName !== 'UL') {
      debouncedUpdateAutoCompleteOptions();
      setAutoCompleteOptions(undefined);
    }
  }

  return (
    <LegalEntityAutoCompleteField
      {...rest}
      options={autoCompleteOptions}
      onChange={handleAutoCompleteChange}
      onSelect={handleAutoCompleteSelect}
      onBlur={handleInputBlur}
    />
  );
};
